import { showError } from '@client/components/ErrorDialog'
import {
  APP_CONFIG_GET_A_NEWS_LETTER_401_REDIRECT,
  APP_CONFIG_GET_A_NEWS_LETTER_404_REDIRECT,
} from '@config/app.config'
import { captureException } from '@client/utils/sentry'

export const parseResponse =
  (returnError = false) =>
  async (response) => {
    if (response.status === 204) {
      return null
    }
    if (response.ok || response.status === 400 || returnError) {
      let json
      try {
        json = await response.json()
      } catch (e) {
        console.log(e)
      }
      return json
    }
    catchErrors(
      {
        message: response.statusText,
        status: response.status,
        body: response.body,
      },
      null,
      returnError
    )
  }

const catchErrors = (error, realError = null, returnError = false) => {
  if (error && error.status === 401) {
    window.location.href = APP_CONFIG_GET_A_NEWS_LETTER_401_REDIRECT
    return
  }
  if (error && error.status === 404) {
    window.location.href = APP_CONFIG_GET_A_NEWS_LETTER_404_REDIRECT
    return
  }
  if (!error.status || error.status >= 500 || realError) {
    if (realError) {
      captureException(realError)
    } else {
      captureException(error)
    }
  }
  if (!returnError) {
    showError(error)
  } else {
    return error
  }
}

const handleResponse = (fn, errorMessage, returnError = false) => {
  return (req, res, next) => {
    return fn(req, res, next)
      .then(parseResponse(returnError))
      .catch((error) => {
        const message =
          typeof error === 'string'
            ? error
            : error && error.message
            ? error.message
            : 'Unknown Error!'
        const status = (error && error.status) || ''
        return catchErrors(
          {
            message: errorMessage || message,
            status,
          },
          error,
          returnError
        )
      })
  }
}

/**
 * For the editor's save requests (FE-1431). Resolves to `{ ok, status, data }`
 * so the editor can tell a failed save from a successful one.
 *
 * A 4xx on save is something the user has to see and act on in the editor:
 * fix a validation error, pause a running autoresponder, upgrade, or learn
 * that the item can no longer be edited. So unlike handleResponse it neither
 * opens the "reconnect" dialog (retrying the same request cannot help) nor
 * redirects on 404 (that would throw away the open document). A 401 still goes
 * to login, and 5xx or a network failure still opens the dialog, whose retry
 * can succeed there.
 */
export const handleSaveResponse = (fn, errorMessage) => {
  return (...args) => {
    return fn(...args)
      .then(async (response) => {
        const { status } = response
        if (status === 401) {
          window.location.href = APP_CONFIG_GET_A_NEWS_LETTER_401_REDIRECT
          return { ok: false, status, data: null }
        }
        let data = null
        if (status !== 204) {
          try {
            data = await response.json()
          } catch (e) {
            data = null
          }
        }
        if (!response.ok && status >= 500) {
          catchErrors({ message: response.statusText, status })
        }
        return { ok: response.ok, status, data }
      })
      .catch((error) => {
        catchErrors(
          {
            message:
              errorMessage || (error && error.message) || 'Unknown Error!',
            status: (error && error.status) || '',
          },
          error
        )
        return { ok: false, status: 0, data: null }
      })
  }
}

/**
 * For requests that belong to one part of the editor, such as the saved
 * content list and saved blocks (FE-1488, FE-1489). Resolves with the parsed
 * body like handleResponse, but on a failure it rejects instead of opening the
 * "reconnect" dialog or redirecting a 404: that dialog is about the document,
 * and its retry would save the document rather than repeat the request. The
 * caller shows the error and its own retry where the data belongs. The error
 * has the HTTP `status` ('' for a network failure) and the parsed error body
 * as `data`. A 401 still goes to login, and 5xx or a network failure is still
 * reported.
 */
export const handleLoadResponse = (fn, errorMessage) => {
  return (...args) => {
    return fn(...args).then(
      async (response) => {
        const { status } = response
        if (status === 401) {
          window.location.href = APP_CONFIG_GET_A_NEWS_LETTER_401_REDIRECT
        }
        if (!response.ok) {
          let data = null
          try {
            data = await response.json()
          } catch (e) {
            data = null
          }
          const error = Object.assign(
            new Error(errorMessage || response.statusText),
            { status, data }
          )
          if (status >= 500) {
            captureException(error)
          }
          throw error
        }
        if (status === 204) {
          return null
        }
        return response.json()
      },
      (error) => {
        captureException(error)
        throw Object.assign(
          new Error(
            errorMessage || (error && error.message) || 'Unknown Error!'
          ),
          { status: '' }
        )
      }
    )
  }
}

export default handleResponse
